import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, TextField } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchPartnerFunction, updatePartnerFunction, fetchPartnerCategory } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { useAuth } from "../../providers/AuthProvider";

export default function PartnerFunctionEditPage() {
  const { businessDomainId, categoryId, functionId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const { companyId } = useAuth();
  const t = (id) => intl.formatMessage({ id });

  const [original, setOriginal] = useState(null);
  const [category, setCategory] = useState(null);
  const [form, setForm] = useState({ partnerFunctionName: "", partnerFunctionDescription: "", url: "" });

  const basePath = `/strategy/${businessDomainId}/partner-categories/${categoryId}/partner-functions`;

  const loadData = useCallback(async () => {
    try {
      const [fn, cat] = await Promise.all([
        fetchPartnerFunction(functionId),
        fetchPartnerCategory(categoryId),
      ]);
      setOriginal(fn);
      setCategory(cat);
      setForm({
        partnerFunctionName: fn.partnerFunctionName || "",
        partnerFunctionDescription: fn.partnerFunctionDescription || "",
        url: fn.url || "",
      });
    } catch (e) { notify.apiError(e); }
  }, [functionId, categoryId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!original) return;
    try {
      await updatePartnerFunction({
        ...original,
        companyId,
        partnerCategoryId: categoryId,
        partnerCategoryName: category?.partnerCategoryName || original.partnerCategoryName || "",
        partnerFunctionName: form.partnerFunctionName,
        partnerFunctionDescription: form.partnerFunctionDescription || null,
        url: form.url || null,
      });
      notify.success(t("message.update.success"));
      navigate(`${basePath}/${functionId}`);
    } catch (err) { notify.apiError(err); }
  };

  if (!original) return null;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("partnerFunction.edit.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 480 }}>
        <TextField fullWidth required sx={{ mb: 3 }}
          label={t("partnerFunction.name")}
          name="partnerFunctionName"
          value={form.partnerFunctionName}
          onChange={handleChange}
        />

        <TextField fullWidth sx={{ mb: 3 }}
          label={t("common.url")}
          name="url"
          value={form.url}
          onChange={handleChange}
          placeholder="https://..."
        />

        <TextField fullWidth multiline rows={3} sx={{ mb: 3 }}
          label={t("partnerFunction.description")}
          name="partnerFunctionDescription"
          value={form.partnerFunctionDescription}
          onChange={handleChange}
        />

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.save")}</Button>
          <Button variant="outlined" onClick={() => navigate(`${basePath}/${functionId}`)}>
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
